import {
  IAppConfigBids,
  IAppConfigDeals,
  IAppConfigGradeReviewRequests,
  IAppConfigLoginAndSignup,
  IAppConfigNotifications,
  IAppConfigProfile,
  IAppConfigTransactions,
} from '../app-config-store';
import { useRegionStore } from '../region-store';

import { useAuthStore } from './useAuthStore';

/**
 * Configuration for a single sidebar menu item.
 * - isEnabled: Whether the item is clickable (false = disabled/grayed out)
 * - isVisible: Whether the item is shown (false = completely hidden)
 */
interface ISidebarMenuItem {
  isEnabled: boolean;
  isVisible: boolean;
}

/**
 * Whether the dashboard section is enabled.
 * TODO: When dashboard section is done, set this to true.
 *
 */
const IS_DASHBOARD_SECTION_ENABLED = false;

/**
 * Whether the bids options menu is enabled.
 */
const IS_BIDS_OPTIONS_MENU_ENABLED = false;

/**
 * Whether the deals options menu is enabled.
 */
const IS_DEALS_OPTION_MENU_ENABLED = false;

/**
 * Configuration for sidebar menu visibility and enabled state.
 */
interface ISidebarFeature {
  accountStatements: ISidebarMenuItem;
  allBids: ISidebarMenuItem;
  allDeals: ISidebarMenuItem;
  allRequests: ISidebarMenuItem;
  allTransactions: ISidebarMenuItem;
  bidEfficiency: ISidebarMenuItem;
  bidsSection: ISidebarMenuItem;
  dashboardRecentActivity: ISidebarMenuItem;
  dashboardSection: ISidebarMenuItem;
  dashboardStatistics: ISidebarMenuItem;
  dealsDelivered: ISidebarMenuItem;
  dealsInTransit: ISidebarMenuItem;
  dealsSection: ISidebarMenuItem;
  gradeReviewHelp: ISidebarMenuItem;
  gradeReviewSection: ISidebarMenuItem;
  myProfile: ISidebarMenuItem;
  myUploads: ISidebarMenuItem;
  notifications: ISidebarMenuItem;
  requestAReview: ISidebarMenuItem;
  settingsSection: ISidebarMenuItem;
  stagedBids: ISidebarMenuItem;
  stagedBidsReport: ISidebarMenuItem;
  transactionsInTransit: ISidebarMenuItem;
  transactionsSection: ISidebarMenuItem;
}

interface IBuyerConfig {
  /**
   * Bids configuration (core settings + feature visibility).
   */
  bids: IAppConfigBids;
  /**
   * Whether to have the items of the sidebar expanded by default
   * To be used for sidebars with less items in the menu.
   * Note that this is currently set to
   */
  hasExpandedSidebarItems: boolean;
  /**
   * Whether to hide features for beta user
   */
  isBetaTester: boolean;
  /**
   * Whether the user can view both live and staged bidding.
   */
  isBothScheduledAndInstantBiddingEnabled: boolean;
  /**
   * Whether the user is a company tenant.
   */
  isCompanyTenant: boolean;
  /**
   * Whether the user can view direct live bidding.
   */
  isInstantBiddingEnabled: boolean;
  /**
   * This is used to determine if the user is a PAAS tenant.
   * Such as NORTHLADDER, MMS, etc.
   */
  isPAASTenant: boolean;
  /**
   * Whether the user can view staged bidding.
   */
  isScheduledBiddingEnabled: boolean;
  /**
   * Whether the user has TIMED_AUCTION enabled (combines SCHEDULED and
   * IMMEDIATE with time restrictions).
   */
  isTimedAuctionEnabled: boolean;
  /**
   * Profile configuration for profile visibility.
   */
  profile: IAppConfigProfile;
  /**
   * Sidebar feature configuration for menu visibility.
   */
  sidebarFeature: ISidebarFeature;
  /**
   * Transactions configuration.
   */
  transactions: IAppConfigTransactions;
  /**
   * Deals configuration.
   */
  deals: IAppConfigDeals;
  /**
   * Grade review requests configuration.
   */
  gradeReviewRequests: IAppConfigGradeReviewRequests;
  /**
   * Configuration for login and signup.
   */
  loginAndSignupConfig: IAppConfigLoginAndSignup;
  /**
   * Whether the user can change the theme (light/dark mode).
   * Defaults to false, but can be controlled via remote configuration.
   */
  canSwitchColorScheme: boolean;
  /**
   * Notifications configuration.
   */
  notifications?: IAppConfigNotifications;
}

/**
 * This hooks exposes the buyer config for options to customize the dealer/buyer
 * application, such as hide features like bidding.
 */
export function useBuyerAppConfig(): IBuyerConfig {
  const { activeSession } = useAuthStore();
  const { tenantConfig } = useRegionStore();

  const {
    canSwitchColorScheme = false,
    isBetaTester = false,
    loginAndSignup: remoteLoginAndSignup,
  } = tenantConfig?.buyerConfig || {};

  const {
    biddingType = 'IMMEDIATE',
    hasChangePasswordPermission = false,
    hasCreateBidsPermission = false,
    hasEditProfilePermission = false,
    hasMemberGrrPermission = false,
    hasProfilePermission = false,
    hasViewAccountStatementPermission = false,
    hasViewAssessmentImagesPermission = false,
    hasViewBidsPermission = false,
    hasViewDealsPermission = false,
    hasViewGrrPermission = false,
    hasViewNotificationsPermission = false,
    hasViewTransactionsPermission = false,
    tenantType,
  } = activeSession?.user || {};

  const canViewGradeReviewRequests =
    hasViewGrrPermission || hasMemberGrrPermission;

  const canViewBids = hasViewBidsPermission;

  const canViewBidsListOptionsMenu = IS_BIDS_OPTIONS_MENU_ENABLED;

  const isExportEnabled = hasViewBidsPermission;

  const {
    isSupportAvailable = false,
    isForgetAccountEnabled = false,
    isSignupEnabled = false,
  } = remoteLoginAndSignup || {};

  const isBothScheduledAndInstantBiddingEnabled = biddingType === 'BOTH';
  const isTimedAuctionEnabled = biddingType === 'TIMED_AUCTION';

  const isInstantBiddingEnabled =
    biddingType === 'IMMEDIATE' ||
    isBothScheduledAndInstantBiddingEnabled ||
    isTimedAuctionEnabled;
  const isScheduledBiddingEnabled =
    biddingType === 'SCHEDULED' || isBothScheduledAndInstantBiddingEnabled;

  const isCompanyTenant = tenantType === 'COMPANY';
  const isPAASTenant = tenantType === 'PAAS';

  const canViewHotDeals = isPAASTenant && isCompanyTenant;
  const canPlaceSingleBid = hasCreateBidsPermission && isCompanyTenant;

  // NOTE: Targets MMS without single bid options. To be moved remote later.
  const hasExpandedSidebarItems = !canPlaceSingleBid;

  const bids: IAppConfigBids = {
    canPlaceSingleBid,
    canViewBidsEfficiency: isInstantBiddingEnabled,
    canViewBidsListOptionsMenu,
    canViewBidsStagedEfficiency: isScheduledBiddingEnabled,
    canViewBidsStagedReport: isScheduledBiddingEnabled,
    canViewHotDeals,
    canViewSimilarBids: isBetaTester,
    canViewStagedBids: isScheduledBiddingEnabled,
    canViewStagedBidsOptionsMenu: IS_BIDS_OPTIONS_MENU_ENABLED,
    isExportEnabled,
    isRankOneBidDisplayEnabled: isInstantBiddingEnabled,
  };

  const loginAndSignupConfig: IAppConfigLoginAndSignup =
    remoteLoginAndSignup || {
      isForgetAccountEnabled,
      isSignupEnabled,
      isSupportAvailable,
    };

  const deals: IAppConfigDeals = {
    canViewAssessmentImages: hasViewAssessmentImagesPermission,
    canViewDealsListOptionsMenu: IS_DEALS_OPTION_MENU_ENABLED,
  };

  const transactions: IAppConfigTransactions = {
    canViewTransactions: hasViewTransactionsPermission,
  };

  const gradeReviewRequests: IAppConfigGradeReviewRequests = {
    canViewGradeReviewRequests,
    canEditGradeReviewRequests: hasMemberGrrPermission,
    canCreateGradeReviewRequests: hasMemberGrrPermission,
  };

  const profile: IAppConfigProfile = {
    canChangePassword: hasChangePasswordPermission,
    isPaymentEditAllowed: hasProfilePermission,
    isProfileEditAllowed: hasEditProfilePermission,
  };

  const sidebarFeature: ISidebarFeature = {
    accountStatements: {
      isEnabled: hasViewAccountStatementPermission,
      isVisible: hasViewAccountStatementPermission,
    },
    allBids: {
      isEnabled: canViewBids,
      isVisible: canViewBids,
    },
    allDeals: {
      isEnabled: hasViewDealsPermission,
      isVisible: hasViewDealsPermission,
    },
    allRequests: {
      isEnabled: canViewGradeReviewRequests,
      isVisible: canViewGradeReviewRequests,
    },
    allTransactions: {
      isEnabled: hasViewTransactionsPermission,
      isVisible: hasViewTransactionsPermission,
    },
    bidEfficiency: {
      isEnabled: isInstantBiddingEnabled,
      isVisible: isInstantBiddingEnabled,
    },
    bidsSection: { isEnabled: canViewBids, isVisible: canViewBids },
    dashboardRecentActivity: {
      isEnabled: IS_DASHBOARD_SECTION_ENABLED,
      isVisible: IS_DASHBOARD_SECTION_ENABLED,
    },
    dashboardSection: {
      isEnabled: IS_DASHBOARD_SECTION_ENABLED,
      isVisible: IS_DASHBOARD_SECTION_ENABLED,
    },
    dashboardStatistics: {
      isEnabled: IS_DASHBOARD_SECTION_ENABLED,
      isVisible: IS_DASHBOARD_SECTION_ENABLED,
    },
    dealsDelivered: {
      isEnabled: hasViewDealsPermission,
      isVisible: hasViewDealsPermission,
    },
    dealsInTransit: {
      isEnabled: hasViewDealsPermission,
      isVisible: hasViewDealsPermission,
    },
    dealsSection: {
      isEnabled: hasViewDealsPermission,
      isVisible: hasViewDealsPermission,
    },
    gradeReviewHelp: { isEnabled: false, isVisible: false },
    gradeReviewSection: {
      isEnabled: canViewGradeReviewRequests,
      isVisible: canViewGradeReviewRequests,
    },
    myProfile: {
      isEnabled: hasProfilePermission,
      isVisible: hasProfilePermission,
    },
    myUploads: { isEnabled: true, isVisible: true },
    notifications: {
      isEnabled: hasViewNotificationsPermission,
      isVisible: hasViewNotificationsPermission,
    },
    requestAReview: {
      isEnabled: hasMemberGrrPermission,
      isVisible: hasMemberGrrPermission,
    },
    settingsSection: { isEnabled: isCompanyTenant, isVisible: isCompanyTenant },
    stagedBids: {
      isEnabled: isScheduledBiddingEnabled && !isTimedAuctionEnabled,
      isVisible: isScheduledBiddingEnabled && !isTimedAuctionEnabled,
    },
    stagedBidsReport: {
      isEnabled: isScheduledBiddingEnabled && !isTimedAuctionEnabled,
      isVisible: isScheduledBiddingEnabled && !isTimedAuctionEnabled,
    },
    transactionsInTransit: { isEnabled: false, isVisible: false },
    transactionsSection: {
      isEnabled: hasViewTransactionsPermission,
      isVisible: hasViewTransactionsPermission,
    },
  };

  return {
    bids,
    canSwitchColorScheme,
    deals,
    gradeReviewRequests,
    hasExpandedSidebarItems,
    isBetaTester,
    isBothScheduledAndInstantBiddingEnabled,
    isCompanyTenant,
    isInstantBiddingEnabled,
    isPAASTenant,
    isScheduledBiddingEnabled,
    isTimedAuctionEnabled,
    loginAndSignupConfig,
    profile,
    sidebarFeature,
    transactions,
  };
}
